import { create } from 'zustand';
import debounce from 'lodash';
import { TalkToJourneyMessage, TalkToJourneyChatHistoryItem, TalkToJourneyResponse, generateUUID } from './types';
import { Journey } from 'src/common/interfaces/journey.interface';
import { apiJourneyBrainAsk } from 'src/utils/journeyApi';
import { enhancedChatApi } from 'src/utils/enhancedChatApi';
import { apiSegmentTrackAnonymous } from 'src/utils/segment';
import { EditorInsightsPdfChartTooltip } from 'src/editor/insights/pdf-chart-tooltip';
import { Nullable } from 'src/types/nullable.type';
import { RenderMode } from '../types';
import { Organization } from 'src/common/interfaces/organization.interface';
import { embedUserEventsFactory } from 'src/dashboard/embed/user-events.factory';
import { getJourneySession, getJourneyTracker } from 'src/utils/tracker';
import { useCurrentSubscription } from 'src/store/subscription';

type StateVars = {
  journeyUUID: string;
  orgSlug: string;
  journeyAlias: Nullable<{
    slug: string;
    uuid: string;
  }>;
  renderMode: Nullable<RenderMode>;
  visitorEmail: string;
  inputValue: string;
  name: string;
  personality: string;
  disabled: boolean;
  shouldGreetVisitors: boolean;
  avatarUrl: string;
  messages: TalkToJourneyMessage[];
  chatHistory: TalkToJourneyChatHistoryItem[];
  thinking: boolean;
  potentialQuestions: string[];
  previouslyAskedQuestions: string[];
  conversationId: string;
  conversationStarted: boolean;
  chatOpen: boolean;
  variablesFromJourneyInitialized: boolean;
}

type StateFns = {
  setInputValue: (inputValue: string) => void;
  setName: (name: string) => void;
  setPersonality: (personality: string) => void;
  setDisabled: (disabled: boolean) => void;
  setShouldGreetVisitors: (shouldGreetVisitors: boolean) => void;
  setAvatarUrl: (avatarUrl: string) => void;
  setMessages: (messages: TalkToJourneyMessage[]) => void;
  setChatOpen: (chatOpen: boolean) => void;
  initChat: (params: any) => void;
  clearChat: () => void;
  initVariablesFromJourney: (journey: Journey, organization?: Nullable<Organization>, renderMode?: RenderMode) => void;
  onSubmitQuestion: (journeyUUID: string, visitorEmail: string, question: string, isPredefined?: boolean) => void;
  generateQuestions: (journeyUUID: string, visitorEmail: string, previouslyAskedQuestions: string[]) => void;
  reset: () => void;
}

type State = StateVars & StateFns;

const initialState: StateVars = {
  journeyUUID: "",
  orgSlug: "",
  journeyAlias: null,
  renderMode: null,
  visitorEmail: "",
  inputValue: "",
  name: "",
  personality: "",
  disabled: true,
  shouldGreetVisitors: false,
  avatarUrl: "",
  messages: [],
  chatHistory: [],
  thinking: false,
  potentialQuestions: [],
  previouslyAskedQuestions: [],
  conversationId: "",
  conversationStarted: false,
  chatOpen: false,
  variablesFromJourneyInitialized: false,
};

export const useTalkToJourneyStore = create<State>((set, get) => ({
  ...initialState,
  setInputValue: (inputValue: string) => {
    set(() => ({
      inputValue,
    }));
  },
  setName: (name: string) => {
    set(() => ({
      name,
    }));
  },
  setPersonality: (personality: string) => {
    set(() => ({
      personality,
    }));
  },
  setDisabled: (disabled: boolean) => {
    set(() => ({
      disabled,
    }));
  },
  setShouldGreetVisitors: (shouldGreetVisitors: boolean) => {
    set(() => ({
      shouldGreetVisitors,
    }));
  },
  setAvatarUrl: (avatarUrl: string) => {
    set(() => ({
      avatarUrl,
    }));
  },
  setMessages: (messages: TalkToJourneyMessage[]) => {
    set(() => ({
      messages,
    }));
  },
  setChatOpen: (chatOpen: boolean) => {
    set(() => ({
      chatOpen,
    }));
  },
  initChat: ({
    journeyUUID = '', 
    visitorEmail = '',
    forceInit = false
  }) => {
    // generate a random conversation uuid once
    const { 
      name, personality, shouldGreetVisitors, 
      generateQuestions, conversationStarted, potentialQuestions,
      journeyAlias, orgSlug, renderMode,
     } = get();
    const { journeyUUID: existingJourneyUUID, visitorEmail: existingVisitorEmail } = get();
    const conversationId = generateUUID();

    if (conversationStarted && !forceInit) {
      return;
    }

    const validJourneyUUID = journeyUUID || existingJourneyUUID;
    const validVisitorEmail = visitorEmail || existingVisitorEmail;

    // create user events factory only on the player side
    const journeyTrackingUUID = getJourneyTracker();
    const journeySessionUUID = getJourneySession();
    const userEventsFactory = (renderMode === 'player' && journeyAlias) ?
      embedUserEventsFactory({
        aliasUUID: journeyAlias.uuid,
        sessionUUID: journeySessionUUID,
        trackingUUID: journeyTrackingUUID,
        email: validVisitorEmail,
      }) : null;

    set(() => ({
      journeyUUID: validJourneyUUID,
      visitorEmail: validVisitorEmail,
      conversationId,
      conversationStarted: true,
      messages: [],
      chatHistory: [],
    }));

    set(() => ({ thinking: true }));
    enhancedChatApi.startConversation({
      journey_uuid: validJourneyUUID,
      visitor_email: validVisitorEmail,
      user_context: {},
    }).then((result: any) => {
      if (result.success && result.data) {
        const { conversation_id, greeting, potential_questions } = result.data;
        const newMessage: TalkToJourneyMessage = {
          role: 'assistant',
          text: greeting.content,
          source_content_uuid: greeting.metadata?.source_content_uuid,
          source_node_uuid: greeting.metadata?.source_node_uuid,
        };
        set(() => ({
          conversationId: conversation_id,
          messages: [newMessage],
          thinking: false,
          potentialQuestions: potential_questions || [],
          conversationStarted: true,
        }));
      } else {
        set(() => ({ thinking: false }));
      }
    }).catch((e) => {
      const fallbackMessage: TalkToJourneyMessage = {
        role: 'assistant',
        text: `Hello! I am ${name}. Welcome to this Journey. I will be your assistant and can answer any question you have about the content...`,
      };
      set(() => ({
        messages: [fallbackMessage],
        thinking: false,
        conversationStarted: true,
      }));
    });
    if (potentialQuestions.length === 0) {
      generateQuestions(validJourneyUUID, validVisitorEmail, []);
    }
  },
  clearChat: () => {
    set(() => ({
      messages: [],
      chatHistory: [],
      conversationStarted: false,
    }));
  },
  initVariablesFromJourney: (journey: Journey, organization?: Nullable<Organization>, renderMode?: RenderMode) => {
    const orgSlug = organization?.slug || journey.organization?.slug;
    const journeyAlias = journey.alias;
    const data = journey.data = journey.data || {};
    const talkToJourneySettings = data['talk_to_journey'] || {};

    // Check if user has access to chatbot based on subscription plan
    const { subscriptionPlanType, isOnTrial } = useCurrentSubscription.getState();
    const hasChatbotAccess = (subscriptionPlanType === 'pro' || subscriptionPlanType === 'enterprise') || isOnTrial;
    
    // Only force-disable for new journeys (when talk_to_journey settings don't exist)
    // For existing journeys, respect the saved state from the backend
    const isNewJourney = !talkToJourneySettings || Object.keys(talkToJourneySettings).length === 0;
    const shouldDisableChatbot = isNewJourney && !hasChatbotAccess;
    const finalDisabledState = shouldDisableChatbot ? true : (talkToJourneySettings.disabled || false);

    set(() => ({
      name: talkToJourneySettings.name || '',
      personality: talkToJourneySettings.personality || '',
      shouldGreetVisitors: talkToJourneySettings.shouldGreetVisitors === true ? true : false,
      disabled: finalDisabledState,
      orgSlug: orgSlug,
      journeyAlias: journeyAlias,
      renderMode: renderMode,
      variablesFromJourneyInitialized: true,
    }));
  },
  generateQuestions: (journeyUUID: string, visitorEmail: string, previouslyAskedQuestions: string[] = []) => {
    const { conversationId, name, personality } = get();
    apiJourneyBrainAsk('init_talk_to_journey', {
      conversation_id: conversationId,
      journey_uuid: journeyUUID,
      previous_questions: previouslyAskedQuestions,
      personality,
      name,
      visitor_email: visitorEmail
    }).then((r) => {
      const response = r.response;
      const { answer } = response;
      // response is a string of potential questions, each question is split by a newline

      const potentialQuestions = answer.split('\n');
      // remove empty strings
      const filteredPotentialQuestions = potentialQuestions.filter((q: string) => q.trim().length > 0);
      // trim any whitespace or - from the beginning or end of the string
      const trimmedPotentialQuestions = filteredPotentialQuestions.map((q: string) => q.trim().replace(/^-/, ''));

      // check if any of the trimmed potential questions are in the previously asked questions

      // each question might have a number in front of them, either 1. 2. or 3.
      // remove the number from the beginning of the string if it exists

      const regex = /^[0-9]+\. /;
      const newPotentialQuestions = trimmedPotentialQuestions.filter((q: string) => !previouslyAskedQuestions.includes(q)).map((q: string) => {
        const match = q.match(regex);
        if (match) {
          return q.replace(match[0], '');
        };

        return q;
      });

      set(() => ({
        potentialQuestions: newPotentialQuestions,
      }));
    });
  },
  onSubmitQuestion: (journeyUUID: string, visitorEmail: string, question: string, isPredefined: boolean = false) => {
    const { 
      orgSlug, journeyAlias,
      name, personality,
      renderMode,
      messages, previouslyAskedQuestions, chatHistory, conversationId, generateQuestions
     } = get();
    question = question.trim();
    const newMessage: TalkToJourneyMessage = {
      role: 'user',
      text: question,
    };
    const updatedMessages = [...messages, newMessage];
    set(() => ({
      messages: updatedMessages,
      potentialQuestions: [],
      inputValue: '',
      thinking: true,
    }));

    // once we add chat history, it needs to be a tuple like this
    // chat_history = [(query, result["answer"])]
    const updatedPreviouslyAskedQuestions = [...previouslyAskedQuestions, question];
    set(()=> ({
      previouslyAskedQuestions: updatedPreviouslyAskedQuestions,
    }));

    // create user events factory only on the player side
    const journeyTrackingUUID = getJourneyTracker();
    const journeySessionUUID = getJourneySession();
    const userEventsFactory = (renderMode === 'player' && journeyAlias) ? 
      embedUserEventsFactory({
        aliasUUID: journeyAlias?.uuid,
        sessionUUID: journeySessionUUID,
        trackingUUID: journeyTrackingUUID,
        email: visitorEmail,
      }) : null;

    enhancedChatApi.sendMessage({
      journey_uuid: journeyUUID,
      visitor_email: visitorEmail,
      message: question,
    }).then((result: any) => {
      if (result.success && result.data) {
        const { answer, source_content_uuid, source_node_uuid, confidence_score, potential_questions } = result.data;
        const newAssistantMessage: TalkToJourneyMessage = {
          role: 'assistant',
          text: answer,
          source_content_uuid,
          source_node_uuid,
        };
        set(() => ({
          messages: [...updatedMessages, newAssistantMessage],
          thinking: false,
          chatHistory: [...chatHistory, { question, answer }],
          potentialQuestions: potential_questions || [],
        }));
        const chatParams = {
          question,
          answer,
          question_type: isPredefined ? 'predefined' : 'user',
          conversation_id: conversationId,
        };
        apiSegmentTrackAnonymous('Talk to Journey Message Exchange', {
          ...chatParams,
          journey_uuid: journeyUUID,
          org_slug: orgSlug,
          journey_alias_slug: journeyAlias?.slug,
          visitor_email: visitorEmail,
        });
        if (userEventsFactory) {
          const chatbotMessageExchangeEvent = userEventsFactory.createChatbotMessageExchangeEvent();
          chatbotMessageExchangeEvent.fire(chatParams);
        }
      } else {
        const fallbackAssistantMessage: TalkToJourneyMessage = {
          role: 'assistant',
          text: 'I am sorry the system is not responding. Please try again in a few seconds.',
        };
        set(() => ({
          messages: [...updatedMessages, fallbackAssistantMessage],
          thinking: false,
          chatHistory: [...chatHistory, { question, answer: '' }],
          potentialQuestions: [],
        }));
      }
    }).catch((e) => {
      const errorAssistantMessage: TalkToJourneyMessage = {
        role: 'assistant',
        text: 'I am sorry the system is not responding. Please try again in a few seconds.',
      };
      set(() => ({
        messages: [...updatedMessages, errorAssistantMessage],
        thinking: false,
        chatHistory: [...chatHistory, { question, answer: '' }],
      }));
      apiSegmentTrackAnonymous('Talk to Journey Message Error', {
        question,
        answer: errorAssistantMessage.text,
        question_type: isPredefined ? 'predefined' : 'user',
        journey_uuid: journeyUUID,
        org_slug: orgSlug,
        journey_alias_slug: journeyAlias?.slug,
        visitor_email: visitorEmail,
      });
    });
  },
  reset: () => {
    set(() => ({
      ...initialState
    }));
  }
}));